<template>
  <div>
    <SettingsPagePanelHeader :showSubtitle="true">
      <template #title>選択肢自動応答設定</template>
      <template #subtitle>
        選択肢の回答をユーザーから受信したときに、ここで設定したメッセージで返信します。
      </template>
      <template #right>
        <RouterLink
          :to="{
            name: 'autoChatReplySettingNew',
          }"
        >
          <FlatButton>新しい条件を作成</FlatButton>
        </RouterLink>
      </template>
    </SettingsPagePanelHeader>
    <div :class="$style.subHeader">
      <div :class="$style.subHeader__message">
        <p v-if="showCalendarUnavailableWarning" :class="$style.warning">
          外部カレンダー連携がされていないため日程調整ができません。<br />
          <RouterLink :to="{ name: 'calendarSetting' }" target="_blank"
            >カレンダー連携設定ページ</RouterLink
          >
          で連携をしてください。
        </p>
      </div>
      <div :class="$style.subHeader__button">
        <FlatButton
          @click="handleDelete"
          :disabled="settingsWithCheck.every(value => !value.checked)"
          variant="caution"
          >削除</FlatButton
        >
      </div>
    </div>
    <ul :class="$style.list">
      <li
        v-for="(setting, index) in settingsWithCheck"
        :key="setting.id"
        :class="$style.list__item"
      >
        <DolphinCheckbox
          :class="$style.list__checkbox"
          :modelValue="setting.checked"
          @update:model-value="checked => handleChangeChecked(index, checked)"
        />
        <router-link
          :to="autoChatReplySettingDetailHref(setting.id)"
          :class="$style['listItem']"
        >
          <div :class="$style['listItem__keyword']">
            <StateChip
              :text="setting.isEnabled ? '有効' : '無効'"
              :variation="setting.isEnabled ? 'positive' : 'negative'"
            />
            <p :class="[$style.keyword__text, $style.text]">
              {{ setting.keyword }}
            </p>
          </div>
          <DolphinChip
            :text="extraTypeString(setting.extra.type)"
            :class="[
              $style.extraChip,
              $style[extraTypeChipSelectorName(setting.extra.type)],
            ]"
          />
        </router-link>
      </li>
    </ul>

    <AlphaFeature>
      <section v-if="isCorporationAdministrator" :class="$style.adminMenu">
        <p :class="$style.adminMenu__title">企業管理者専用機能</p>
        <router-link
          :to="{
            name: 'autoChatReplySettingCopy',
          }"
        >
          <p :class="$style.adminMenu__link">
            自動応答チャットメッセージの複製
          </p>
        </router-link>
      </section>
    </AlphaFeature>
  </div>
</template>

<script setup lang="ts">
import DolphinCheckbox from '@/components/atoms/DolphinCheckbox.vue'
import DolphinChip from '@/components/atoms/DolphinChip.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import StateChip from '@/components/atoms/StateChip.vue'
import { useAlert } from '@/libs/alertPortal'
import { AutoChatReplySettingExtraType } from '@/types/schema.generated'
import { captureException } from '@sentry/vue'
import { computed, ref } from 'vue'
import type { RouteLocationRaw } from 'vue-router'
import SettingsPagePanelHeader from '../SettingsPagePanelHeader.vue'
import {
  useDeleteAutoChatReplySettingsMutation,
  useFetchAutoChatReplySettingsQuery,
} from './AnswerMessageAutoChatReplyView.generated'
import { useCalendar } from '@/hooks/useCalendar'
import { AlphaFeature } from '@/hooks/useTestMode'
import { useLogger } from '@/logger/logger'
import { useRoute } from 'vue-router'

const route = useRoute('autoChatReplySetting')

const { result, refetch } = useFetchAutoChatReplySettingsQuery({
  fetchPolicy: 'no-cache',
})
const settings = computed(
  () => result.value?.viewer.autoChatReplySettings ?? []
)

const isCorporationAdministrator = computed(
  () => result.value?.uiControl.editCorporation ?? false
)

const settingsWithCheck = computed(() =>
  settings.value.map(setting => ({
    ...setting,
    checked: checkedSettingIds.value.includes(setting.id),
  }))
)
const checkedSettingIds = ref<string[]>([])
const handleChangeChecked = (index: number, checked: boolean) => {
  if (checked) {
    checkedSettingIds.value = Array.from(
      new Set([...checkedSettingIds.value, settings.value[index].id])
    )
    return
  }
  checkedSettingIds.value = checkedSettingIds.value.filter(
    id => id !== settings.value[index].id
  )
}

const autoChatReplySettingDetailHref = (id: string): RouteLocationRaw => {
  return {
    name: 'autoChatReplySettingDetail',
    params: {
      entranceGUID: route.params.entranceGUID,
      autoChatReplySettingId: id,
    },
  }
}

const extraTypeString = (type: AutoChatReplySettingExtraType): string => {
  switch (type) {
    case AutoChatReplySettingExtraType.None:
      return '本文のみ'
    case AutoChatReplySettingExtraType.Question:
      return '選択肢付き'
    case AutoChatReplySettingExtraType.BookingRequest:
      return '日程調整'
  }
}

const extraTypeChipSelectorName = (
  type: AutoChatReplySettingExtraType
): string => {
  switch (type) {
    case AutoChatReplySettingExtraType.None:
      return 'extraChip--none'
    case AutoChatReplySettingExtraType.Question:
      return 'extraChip--question'
    case AutoChatReplySettingExtraType.BookingRequest:
      return 'extraChip--bookingRequest'
  }
}

// 日程調整の自動応答チャット設定があるとき、外部カレンダー連携している人がいないときに警告を出す
const { isCalendarAvailable } = useCalendar()
const showCalendarUnavailableWarning = computed(() => {
  return (
    settings.value.some(
      setting =>
        setting.extra.type === AutoChatReplySettingExtraType.BookingRequest
    ) && !isCalendarAvailable.value
  )
})

const alert = useAlert()
const logger = useLogger()
const { mutate } = useDeleteAutoChatReplySettingsMutation({})

const handleDelete = async () => {
  if (!window.confirm('選択した設定を削除しますか？')) {
    return
  }
  try {
    const deletedSettings = settings.value
      .filter(setting => checkedSettingIds.value.includes(setting.id))
      .map(setting => ({
        id: Number(setting.id),
        isEnabled: setting.isEnabled,
        keyword: setting.keyword,
        body: '',
        extra: setting.extra,
      }))
    await mutate({
      ids: checkedSettingIds.value,
    })
    logger.ltlPeople.event.deleteAutoChatReplySettings(logger.buildMeta(), {
      data: deletedSettings,
    })
  } catch (e) {
    alert.error('削除に失敗しました、もう一度お試しください')
    console.error(e)
    captureException(e)
    return
  }
  refetch()
  alert.success('削除しました')
}
</script>

<style lang="scss" module>
.info {
  margin-bottom: 16px;
  font-size: 12px;
}

.subHeader {
  display: flex;
  align-items: center;
  gap: 32px;
  padding: 0 0 8px;
  width: 100%;
  justify-content: space-between;

  &__message {
    font-size: 16px;
  }

  &__button {
    display: flex;
    justify-content: flex-end;
    width: 200px;
  }
}

.warning {
  font-weight: bold;
  color: $color-caution-accent;
}

.list {
  display: flex;
  flex-direction: column;
  gap: 4px;

  &:nth-child(n + 2) {
    margin-top: 12px;
  }

  &__item {
    &:hover {
      background-color: $color-surface-accent-primary-light;
    }
  }

  &__checkbox {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 16px;
    pointer-events: auto;
  }
}

.listItem {
  border: 1px solid $color-border-low-emphasis;
  border-radius: $border-radius-v2-base;
  display: flex;
  align-items: center;
  padding: 16px 16px 16px 56px;
  justify-content: space-between;

  &:hover {
    text-decoration: none;
  }

  &__keyword {
    display: flex;
    gap: 16px;
  }
}

.text {
  color: $color-okaneko-blue;
  font-weight: bold;
}

.extraChip {
  font-weight: 700;
  color: $color-text-and-object-high-emphasis-inverse;

  &--none {
    background-color: $color-label-orange;
  }

  &--question {
    background-color: $color-label-yellow;
  }

  &--bookingRequest {
    background-color: $color-label-water;
  }
}

.adminMenu {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__title {
    font-weight: 600;
    font-size: 14px;
  }

  &__link {
    font-size: 12px;
  }

  &:nth-child(n + 2) {
    margin-top: 24px;
  }
}
</style>
